// SAB-466: Locance geo-compliance FRONT-END gather — script loader.
//
// Loads the two in-page Locance scripts the geo-check needs:
//   1. HTML5 Location collector  — exposes global `lshtml5(...)`
//   2. Device-Profiling toolkit  — exposes global `lsdp` (with `.dp(...)`)
//
// Both are script-in-page (like PayPal's `window.paypal` and Persona's
// `window.Persona`), NOT cross-origin iframes: `lshtml5` is invoked directly
// with a by-reference JS callback that must fire in our document, and
// `lsdp.dp()` reads a global the profiling script defines here. Because the
// collector calls navigator.geolocation from our top document, the only
// Permissions-Policy grant required is geolocation=(self) (see vercel.json,
// SAB-466 commit 1).
//
// Loader shape mirrors loadPersonaSdk() (NFLFundsSection.tsx:82-112): a
// module-scoped promise cache so a component re-mount never re-injects, plus
// reuse of an already-present <script> tag.
//
// Hosts/ids are env-driven (VITE_LOCANCE_*, build-time baked, branch-scoped).
// Defaults below are the Locance DEV values; PROD supplies the first-party
// proxy subdomains via Vercel Production env before the build.

/* ── Env-driven config (dev fallbacks; prod set via Vercel build env) ── */

// SAB-490/493 geo master switch. The Locance geo gate is called at the money
// moments ONLY when this is true. Default OFF (any value other than the literal
// 'true', including unset) so the gate must be explicitly opted into per
// deployment. Staging sets VITE_GEO_ENABLED=true; PROD stays OFF until SAB-488
// (William enables the prod Locance OAuth app), then flips to true at cutover.
// OFF means callers short-circuit to "allowed" and Locance is never invoked —
// deposits/withdrawals/joins behave exactly as before this gate existed (no
// fail-close). The separate profile-eligibility (declared-state) layer is
// independent of this flag and keeps running.
//
// GUARD (this naming caused the SAB-600 missing_session outage, and will again):
// GEO_ENABLED controls the client-side BLOCKING UI ONLY (local fail-close +
// GeoBlockScreen). It does NOT decide whether to GATHER a session. Any surface that
// needs to GATHER and send a geo_session_id MUST gate on GEO_ENABLED || GEO_OBSERVE
// (use acquireGeoSession in src/lib/acquireGeoSession.ts), never GEO_ENABLED alone.
// A surface gated on GEO_ENABLED alone gathers nothing on prod (GEO_ENABLED false,
// GEO_OBSERVE true) and the server fail-closes every request with missing_session.
export const GEO_ENABLED = import.meta.env.VITE_GEO_ENABLED === 'true';

// SAB-488 monitor/observe mode (client half). When ON (and GEO_ENABLED OFF), the
// client STILL gathers a fresh Locance session and sends geo_session_id — so the
// server can record the real verdict in geo_verdicts — but it NEVER fail-closes
// (never blocks the money action, never shows GeoBlockScreen). GEO_ENABLED
// (enforce) takes precedence: if both are set, the client enforces as normal.
//
// GUARD: with the server's GEO_ENFORCE on, setting VITE_GEO_OBSERVE=false silently
// breaks the deposit path. Gathering gates on GEO_ENABLED || GEO_OBSERVE, so with
// both false the client sends no session and the server fail-closes every deposit
// with missing_session. Do not turn GEO_OBSERVE off while GEO_ENFORCE is on.
export const GEO_OBSERVE = import.meta.env.VITE_GEO_OBSERVE === 'true';

const HTML5_HOST =
  (import.meta.env.VITE_LOCANCE_HTML5_HOST as string | undefined) ??
  'bl1.locance.net';

// The Device-Profiling toolkit (ThreatMetrix) is SELF-HOSTED and renamed under
// /public per Locance guidance (dodges ad-blocker/privacy filter lists that
// target the vendor CDN + filename). It is served same-origin, so there is NO
// profiling script HOST — dp1.locance.net survives only as the profiling
// *domain* argument to lsdp.dp() below.
const PROFILING_FILE =
  (import.meta.env.VITE_LOCANCE_PROFILING_FILE as string | undefined) ??
  'sf-session.js';

// The domain arg passed to lsdp.dp(domain, org, sessionId) — where ThreatMetrix
// sends its profiling beacons. NOT a script host. dev/staging: dp1.locance.net.
export const LOCANCE_PROFILING_DOMAIN =
  (import.meta.env.VITE_LOCANCE_PROFILING_DOMAIN as string | undefined) ??
  'dp1.locance.net';

// Org id for lsdp.dp(). dev/staging = Locance internal dev org 'wh2hgf17';
// prod = '34mvnza6' (supply VITE_LOCANCE_ORG_ID=34mvnza6 on the prod build).
//
// SAB-466 ROOT CAUSE (2 of the 5): compliance silently fails unless BOTH of
// these point at the REAL registered values —
//   • LOCANCE_ORG_ID must be the real org '34mvnza6' in prod. The 'wh2hgf17'
//     fallback is Locance's internal DEV tenant; leaving it in place makes
//     profiling resolve against the wrong org.
//   • consentDomain (defined in src/hooks/useLocanceGeoCheck.ts) must be a FQDN
//     REGISTERED to the Locance service. The html5 location session is keyed by
//     that domain, so an unregistered host — e.g. a raw *.vercel.app preview —
//     yields "HTML5 Session Id does not exist". Set VITE_LOCANCE_CONSENT_DOMAIN
//     to a registered FQDN on previews; real *.sabotagefantasy.com hosts already
//     resolve via the host-based fallback.
export const LOCANCE_ORG_ID =
  (import.meta.env.VITE_LOCANCE_ORG_ID as string | undefined) ?? 'wh2hgf17';

// HTML5 collector path/version is stable across hosts (the prod proxy mirrors
// the origin path). Kept here rather than in env since it's not deployment-
// specific.
const HTML5_PATH = '/v4/html5/?version=v3';

export const LOCANCE_HTML5_SRC = `https://${HTML5_HOST}${HTML5_PATH}`;
// Same-origin, self-hosted (leading slash → served from our own origin on
// preview / staging / prod alike). Strip any leading slash from the env value
// so we don't produce a double slash.
export const LOCANCE_PROFILING_SRC = `/${PROFILING_FILE.replace(/^\/+/, '')}`;

/* ── Minimal globals the scripts install (typed for callers) ── */

/**
 * HTML5 Location collector callback payload (verified against the real Locance
 * shape). Success = `statusCode === 0` (number, top-level) AND
 * `locationData.code === "0"` (STRING) — see useLocanceGeoCheck. Extra vendor
 * fields pass through untyped.
 */
export interface LocanceHtml5Result {
  statusCode?: number;
  locationData?: {
    /** STRING "0" on success — not a number. */
    code?: string;
    [key: string]: unknown;
  };
  message?: string;
  consentState?: string;
  [key: string]: unknown;
}

declare global {
  interface Window {
    // (sessionId, gps, retries, timeoutMs, callback, consentDomain, ...flags)
    lshtml5?: (...args: unknown[]) => void;
    lsdp?: {
      // (profilingDomain, org, sessionId) — domain is where ThreatMetrix beacons
      // go, NOT the script host (the script is self-hosted same-origin).
      dp: (domain: string, org: string, sessionId: string) => void;
      [key: string]: unknown;
    };
    // ThreatMetrix invokes this bare global when device profiling finishes for a
    // session. We define it ONCE (see installTmxCallback) and route by sessionId.
    tmx_profiling_complete?: (sessionId: string) => void;
  }
}

/* ── Loader (mirrors loadPersonaSdk: cached promise + tag reuse) ── */

function injectScript(src: string, readyCheck: () => boolean): Promise<void> {
  if (readyCheck()) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    // A previous mount may have inserted the tag before the global was ready.
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error(`Locance script failed to load: ${src}`)),
      );
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Locance script failed to load: ${src}`));
    document.head.appendChild(script);
  });
}

// Module-scoped caches: a re-mount must not re-inject either script.
let html5Promise: Promise<void> | null = null;
let profilingPromise: Promise<void> | null = null;

function loadHtml5(): Promise<void> {
  if (window.lshtml5) return Promise.resolve();
  if (!html5Promise) {
    html5Promise = injectScript(LOCANCE_HTML5_SRC, () => !!window.lshtml5).catch(
      (err) => {
        html5Promise = null; // allow retry on a later attempt
        throw err;
      },
    );
  }
  return html5Promise;
}

function loadProfiling(): Promise<void> {
  if (window.lsdp) return Promise.resolve();
  // Define the completion callback BEFORE the toolkit runs so a fast profile
  // can't fire before we're listening.
  installTmxCallback();
  if (!profilingPromise) {
    profilingPromise = injectScript(
      LOCANCE_PROFILING_SRC,
      () => !!window.lsdp,
    ).catch((err) => {
      profilingPromise = null; // allow retry on a later attempt
      throw err;
    });
  }
  return profilingPromise;
}

/**
 * Load the Locance scripts. The HTML5 Location collector is REQUIRED — its
 * load failure rejects (the geo-check hard-fails upstream). The Device-
 * Profiling toolkit is a SOFT signal: if it 404s / is ad-blocked, we swallow
 * the error and continue, and startProfiling() will simply resolve false so
 * the backend is called without dProfileSessionId. Locance's verdict — not a
 * missing profile — is the authoritative gate.
 */
export async function loadLocanceScripts(): Promise<void> {
  await loadHtml5(); // fatal on failure
  await loadProfiling().catch(() => {
    // non-fatal: profiling is best-effort. Absent lsdp is handled by
    // startProfiling (resolves false → no dProfileSessionId).
  });
}

/**
 * SAB-644: native-path loader. The NATIVE app submits location via the appData
 * interface (see locanceAppData.ts), NOT the HTML5 collector, so window.lshtml5
 * is unused there — we must NOT load it as a fatal dependency. We still load the
 * device-profiling toolkit (lsdp) best-effort, because profiling (dProfileSessionId)
 * runs on native exactly as on web and the compliance gate references it.
 */
export async function loadLocanceProfilingOnly(): Promise<void> {
  await loadProfiling().catch(() => {
    // non-fatal, same contract as loadLocanceScripts: absent lsdp → startProfiling
    // resolves false → the verdict is computed without dProfileSessionId.
  });
}

/* ── Device profiling (ThreatMetrix) — SOFT signal ── */
//
// One global window.tmx_profiling_complete, defined once, routes completion by
// sessionId to the waiting run via this registry. This lets a single global
// callback serve concurrent/repeated runs without clobbering.

const profilingWaiters = new Map<string, () => void>();
let tmxCallbackInstalled = false;

function installTmxCallback(): void {
  if (tmxCallbackInstalled || typeof window === 'undefined') return;
  tmxCallbackInstalled = true;
  window.tmx_profiling_complete = (sessionId: string) => {
    const resolve = profilingWaiters.get(sessionId);
    if (resolve) {
      profilingWaiters.delete(sessionId);
      resolve();
    }
  };
}

// Locance guidance: wait ~7s for the profile. Non-blocking — a timeout just
// means "no dProfileSessionId", not a block.
const PROFILING_TIMEOUT_MS = 7_000;

/**
 * Fire device profiling for `sessionId` and report whether it completed within
 * the timeout. NEVER rejects and NEVER blocks the money flow:
 *   - resolves true  → tmx_profiling_complete fired → caller sends dProfileSessionId
 *   - resolves false → timeout / lsdp absent / dp() threw → caller omits it
 * The toolkit is loaded ONCE elsewhere; here we only call lsdp.dp() per run.
 */
export function startProfiling(sessionId: string): Promise<boolean> {
  installTmxCallback();
  return new Promise<boolean>((resolve) => {
    let done = false;
    let timer = 0;
    const settle = (completed: boolean) => {
      if (done) return;
      done = true;
      profilingWaiters.delete(sessionId);
      if (timer) window.clearTimeout(timer);
      resolve(completed);
    };

    if (!window.lsdp) {
      // Toolkit didn't load (404 / blocked). Soft-fail immediately.
      settle(false);
      return;
    }

    profilingWaiters.set(sessionId, () => settle(true));
    timer = window.setTimeout(() => settle(false), PROFILING_TIMEOUT_MS);
    try {
      window.lsdp.dp(LOCANCE_PROFILING_DOMAIN, LOCANCE_ORG_ID, sessionId);
    } catch {
      settle(false); // dp() threw — treat as no-profile, non-blocking
    }
  });
}

/** Visible-for-testing: reset the injection caches. Production never needs this. */
export function _resetLocanceLoaderForTesting(): void {
  html5Promise = null;
  profilingPromise = null;
}
